/**
 * Lightweight diagnostics for a failure we cannot observe from outside: this
 * app runs as a hidden cross-origin iframe, so when it stops working the agent
 * sees only a blank frame and no request traffic. Nothing in the network log
 * distinguishes "the poll loop was torn down" from "the frame's renderer
 * process died".
 *
 * Two mechanisms, deliberately chosen for what survives each failure:
 *
 *   1. sessionStorage breadcrumbs. sessionStorage is committed synchronously
 *      and is retained for the tab, so it survives a renderer crash and a
 *      React unmount. After a failure you can read the last recorded state
 *      out of the frame even though the JS that wrote it is long gone.
 *
 *   2. Global error / unhandledrejection listeners. React error boundaries
 *      only catch errors thrown during render, commit, and lifecycle. Errors
 *      in async callbacks, event handlers, and rejected promises bypass them
 *      entirely, and an uncaught one is a prime suspect for a tree that
 *      unmounted itself.
 *
 * Everything here is best-effort and must never throw: diagnostics that can
 * break the app they are diagnosing are worse than no diagnostics.
 */

const STORAGE_KEY = 'copilot:diagnostics';
/**
 * Ring buffer size. Sized to comfortably span the multi-minute degradation
 * window seen before the frame goes quiet, while staying far below the ~5MB
 * sessionStorage budget.
 */
const MAX_BREADCRUMBS = 200;

/**
 * Upper bound on error reports forwarded over the metrics socket per page load.
 * Breadcrumbs and console output are uncapped; only the network side is.
 */
const MAX_FORWARDED_REPORTS = 20;

export interface Breadcrumb {
  /** Wall clock, so gaps are visible even if the page was suspended. */
  at: string;
  /** ms since page load, for comparing against wall clock to spot suspension. */
  uptimeMs: number;
  event: string;
  detail?: Record<string, unknown>;
}

let installed = false;

/**
 * In-memory source of truth. sessionStorage is a write-through mirror so the
 * trail survives the frame dying, but we never read it back on the hot path:
 * this runs on every poll on a hidden iframe's main thread, and parsing the
 * whole buffer 20 times a minute is exactly the kind of wasted main-thread
 * work that feeds browser throttling.
 */
let buffer: Breadcrumb[] = [];
/** False once we learn sessionStorage is unusable, so we stop retrying it. */
let storageUsable = true;

const persist = (): void => {
  if (!storageUsable) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // Quota exceeded, or storage blocked by partitioning in a third-party
    // frame. Breadcrumbs stay in memory; they just won't survive a crash.
    storageUsable = false;
  }
};

const readPersisted = (): Breadcrumb[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Breadcrumb[]) : [];
  } catch {
    return [];
  }
};

/**
 * Record a breadcrumb. Safe to call from anywhere, including inside error
 * handlers and timer callbacks.
 */
export const breadcrumb = (event: string, detail?: Record<string, unknown>): void => {
  try {
    buffer.push({
      at: new Date().toISOString(),
      uptimeMs: Math.round(performance.now()),
      event,
      ...(detail ? { detail } : {}),
    });
    // Trim from the front so the most recent activity always survives.
    if (buffer.length > MAX_BREADCRUMBS) buffer = buffer.slice(-MAX_BREADCRUMBS);
    persist();
  } catch {
    // Never let diagnostics break the caller.
  }
};

/**
 * All breadcrumbs, oldest first. Prefers the in-memory buffer; falls back to
 * whatever a previous page load left in sessionStorage, which is how you read
 * the trail from before a reload.
 */
export const getBreadcrumbs = (): Breadcrumb[] =>
  buffer.length > 0 ? [...buffer] : readPersisted();

/**
 * Print the breadcrumb trail as a table. Reachable from the console via
 * `window.__copilotDiagnostics.dump()` once {@link installDiagnostics} has run,
 * which is how you inspect a frame that has already failed.
 */
export const dumpBreadcrumbs = (): Breadcrumb[] => {
  const all = getBreadcrumbs();
  // eslint-disable-next-line no-console
  console.table(
    all.map((b) => ({
      at: b.at,
      uptimeMs: b.uptimeMs,
      event: b.event,
      detail: b.detail ? JSON.stringify(b.detail) : '',
    })),
  );
  return all;
};

export const clearBreadcrumbs = (): void => {
  buffer = [];
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
};

const describeError = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      // Truncated: breadcrumbs live in sessionStorage and a full minified
      // stack can be several KB.
      stack: error.stack?.slice(0, 1500),
    };
  }
  return { message: String(error).slice(0, 500) };
};

/**
 * Install global error capture. Idempotent.
 *
 * `onFatal` is invoked for uncaught errors and rejections so the caller can
 * forward them over the metrics channel. It is called inside a try/catch —
 * a reporting failure must not escape a global error handler.
 */
export const installDiagnostics = (
  onFatal?: (event: string, detail: Record<string, unknown>) => void,
): void => {
  if (installed) return;
  installed = true;

  // A render error can retrigger every frame, and an unhandled rejection in a
  // poll loop retriggers on every poll. Cap forwarding so a fault can't turn
  // into a flood on the chat WebSocket; breadcrumbs keep recording regardless.
  let reportsSent = 0;

  const report = (event: string, detail: Record<string, unknown>) => {
    breadcrumb(event, detail);
    // eslint-disable-next-line no-console
    console.error(`[diagnostics] ${event}`, detail);
    if (reportsSent >= MAX_FORWARDED_REPORTS) return;
    reportsSent += 1;
    try {
      onFatal?.(event, detail);
    } catch {
      // Reporting is best-effort.
    }
  };

  window.addEventListener('error', (e: ErrorEvent) => {
    report('window.error', {
      ...describeError(e.error ?? e.message),
      source: e.filename,
      line: e.lineno,
      column: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    report('unhandledrejection', describeError(e.reason));
  });

  // A pagehide breadcrumb tells us the document was unloaded or navigated,
  // which cleanly separates "someone replaced/removed the iframe" from "the
  // renderer died" (a crash produces no pagehide) and from "React unmounted
  // but the document stayed alive".
  window.addEventListener('pagehide', (e: PageTransitionEvent) => {
    breadcrumb('pagehide', { persisted: e.persisted });
  });

  window.addEventListener('visibilitychange', () => {
    breadcrumb('visibilitychange', { visibilityState: document.visibilityState });
  });

  breadcrumb('diagnostics.installed', {
    url: window.location.href.slice(0, 300),
    visibilityState: document.visibilityState,
    // If false, sessionStorage is blocked (likely storage partitioning in a
    // third-party frame) and breadcrumbs will NOT survive the frame dying —
    // which is the one case they exist for. Check this before trusting an
    // empty trail as evidence.
    breadcrumbsPersist: storageUsable,
  });

  // Console handle for inspecting a frame that has already stopped working.
  (window as unknown as Record<string, unknown>).__copilotDiagnostics = {
    dump: dumpBreadcrumbs,
    get: getBreadcrumbs,
    clear: clearBreadcrumbs,
  };
};

/** Test seam: allow installDiagnostics to run again. */
export const __resetDiagnosticsForTests = (): void => {
  installed = false;
  buffer = [];
  storageUsable = true;
};
